import { Component, computed, inject, linkedSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { DialogModule } from 'primeng/dialog'; // cspell:disable-line
import { ButtonModule } from 'primeng/button'; // cspell:disable-line
import { RadioButtonModule } from 'primeng/radiobutton'; // cspell:disable-line
import { LucideAngularModule } from 'lucide-angular';
import { PackLimitationsService } from 'src/app/shared/services/pack-limitations.service';
import { TripLimitIndicatorComponent } from 'src/app/shared/components/trip-limit-indicator/trip-limit-indicator.component';
import { ApplyTipsTarget } from '../../../models/reduction-tip';
import { ReductionTipsService } from '../../../services/reduction-tips.service';
import { ImpactService } from '../../../services/impact.service';

/**
 * Asked before the selected tips are written: over the trip itself, or into a
 * copy that leaves the original as it was. A copy takes a trip of the pack, so
 * once none is left the option locks, and the pack's gauge shows with the way
 * to get more.
 */
@Component({
  selector: 'app-reduction-apply-dialog',
  standalone: true,
  imports: [
    FormsModule,
    TranslocoModule,
    DialogModule,
    ButtonModule,
    RadioButtonModule,
    LucideAngularModule,
    TripLimitIndicatorComponent,
  ],
  templateUrl: './reduction-apply-dialog.component.html',
})
export class ReductionApplyDialogComponent {
  protected tipsService = inject(ReductionTipsService);
  protected packLimitations = inject(PackLimitationsService);
  private impactService = inject(ImpactService);
  private translocoService = inject(TranslocoService);

  readonly canCopy = this.packLimitations.canCreateTrip;

  /** Back to overwriting — what the button always did — each time it opens. */
  private chosen = linkedSignal<boolean, ApplyTipsTarget>({
    source: this.tipsService.applyDialogVisible,
    computation: () => 'overwrite',
  });

  /** The choice, unless the pack has no trip left for a copy. */
  target = computed<ApplyTipsTarget>(() =>
    this.chosen() === 'copy' && this.canCopy() ? 'copy' : 'overwrite',
  );

  selectedCount = computed(() => this.tipsService.selectedTipIds().size);

  tripTitle = computed(
    () =>
      this.impactService.trip()?.title?.trim() ||
      this.translocoService.translate('travelBoards.untitledTrip'),
  );

  choose(target: ApplyTipsTarget): void {
    this.chosen.set(target);
  }

  confirm(): void {
    this.tipsService.applyTips(this.target());
  }

  /** Not while saving: the outcome would land on a dialog no longer there. */
  close(): void {
    if (!this.tipsService.isApplying()) this.tipsService.closeApplyDialog();
  }
}
